import { useRef } from 'react';

/**
 * Tracks the previous iteration of whatever value you provide. The difference between this and usePrevious is that
 * this is render agnostic. It will only return a new value if the current value is updated. usePrevious will return
 * whatever the value was on the previous render which is rarely what you want.
 * @param value The value to track
 * @param compareFn By default we use a shallow comparison however this allows you to implement any comparison you want
 * @returns The previous value
 */
export default function usePreviousValue<T>(
  value: T,
  compareFn?: (prevValue: T | undefined, value: T) => boolean,
): T | undefined {
  const prevValueRef = useRef<T | undefined>(undefined);
  const currentValueRef = useRef<T | undefined>(undefined);
  compareFn = compareFn ?? Object.is;

  if (!currentValueRef.current && value) {
    currentValueRef.current = value;
  }
  if (!compareFn(currentValueRef.current, value)) {
    prevValueRef.current = currentValueRef.current;
    currentValueRef.current = value;
  }

  return prevValueRef.current;
}
